import { DocumentNode, gql, useQuery } from '@apollo/client';
import { get } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatCashSweepRatesValue,
  getInterpolatedKeys,
  getInterpolatedLinkKeys,
  getQueries,
  isCashSweepRatesQuery,
  replaceInterpolatedLinks,
  replaceInterpolatedValues,
} from '~/app/components/universal-text/lib/interpolation-helper';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';

import { buttonUrlWithCustomQueryParams } from '../../dynamic-grid-helpers';
import {
  useDynamicAnalytics,
  addCtaApplyNowLinkTracking,
} from '../../segment/use-dynamic-analytics';

export const useDynamicTextInterpolation = (
  text: string,
  marketplaceOfferId: string,
  pressReleaseId: string,
  interpolationSource: string[],
  interpolatedLinkItems?: {
    impressionPosition: string;
    impressionSection: string;
    key: string;
    value: string;
  }[],
  enableApplyNowButtonTracking = false,
  captureCustomQueryParams = false
) => {
  const searchParams = useSearchParams();
  const { marketplaceOffers, pressReleases } = useDynamicGrid();
  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
  }, [marketplaceOffers, marketplaceOfferId]);

  const selectedPressRelease = useMemo(() => {
    return pressReleases?.[pressReleaseId];
  }, [pressReleases, pressReleaseId]);

  const validGraphQLQuery = useRef<DocumentNode | string>('');
  const [queryData, setQueryData] = useState<any>({});

  const interpolatedKeys = getInterpolatedKeys(text);
  const interpolatedLinkKeys = getInterpolatedLinkKeys(text);

  const { marketplaceButtonProps } = useDynamicAnalytics({
    selectedMarketplaceOffer,
  });

  const DUMMY_QUERY = gql`
    query {
      __typename
    }
  `;

  const matchedValues = useMemo(() => {
    const isCashSweepQuery = isCashSweepRatesQuery(interpolatedKeys || []);

    // Filter out 'interpolatedLink' since it only applies to <<...>> patterns, not {{...}}
    const valueInterpolationSources = interpolationSource.filter(
      (source) => source !== 'interpolatedLink'
    );

    return interpolatedKeys?.map((key: string) => {
      let value: any = '';

      /*
       * If the interpolation source is both marketplace offer and press release,
       * we need to check both sources for the value.
       */
      if (valueInterpolationSources.length === 2) {
        const keyType = key.split('.')[0];
        const keyValue = key.split('.')[1];

        if (keyType === 'marketplaceOffer') {
          value = get(selectedMarketplaceOffer, keyValue);
        } else if (keyType === 'pressRelease') {
          value = get(selectedPressRelease, keyValue);
        }
      } else if (valueInterpolationSources.length === 1) {
        /*
         * If the interpolation source is only one, we consider that as the only source for the value.
         */
        if (valueInterpolationSources[0] === 'marketplaceOffer') {
          value = get(selectedMarketplaceOffer, key);
        } else if (valueInterpolationSources[0] === 'pressRelease') {
          value = get(selectedPressRelease, key);
        } else if (valueInterpolationSources[0] === 'graphQlQuery') {
          if (!validGraphQLQuery.current) {
            validGraphQLQuery.current = getQueries(
              interpolatedKeys as unknown as never[]
            );
          }

          if (queryData) {
            value = get(queryData, key);
          }
        }
      }

      // Only apply special formatting for cashSweepRates queries
      if (isCashSweepQuery && valueInterpolationSources[0] === 'graphQlQuery') {
        return formatCashSweepRatesValue(value, key);
      }

      return value as string;
    });
  }, [
    interpolatedKeys,
    interpolationSource,
    selectedMarketplaceOffer,
    selectedPressRelease,
    queryData,
  ]);

  const query = useMemo(() => {
    return validGraphQLQuery.current || DUMMY_QUERY;
  }, [validGraphQLQuery, DUMMY_QUERY]);

  const { data } = useQuery(query as any, {
    skip: !validGraphQLQuery.current,
    fetchPolicy: 'network-only', // first request
    nextFetchPolicy: 'cache-first', // each subsequent request
  });

  useEffect(() => {
    if (data) {
      setQueryData(data);
    }
  }, [data]);

  // Replace {{...}} patterns with their values
  const textAfterValueInterpolation = replaceInterpolatedValues(
    text,
    matchedValues
  );

  // Process <<...>> link interpolations
  const linkReplacements = useMemo(() => {
    if (!interpolationSource.includes('interpolatedLink')) {
      return [];
    }

    // Helper function to get interpolated value for a single key
    const getInterpolatedValueForKey = (
      key: string,
      valueInterpolationSources: string[]
    ): string => {
      // Handle multiple sources (with prefixes like 'marketplaceOffer.name')
      if (valueInterpolationSources.length === 2) {
        const [keyType, keyValue] = key.split('.');

        if (keyType === 'marketplaceOffer') {
          return get(selectedMarketplaceOffer, keyValue) || '';
        }
        if (keyType === 'pressRelease') {
          return get(selectedPressRelease, keyValue) || '';
        }
        return '';
      }

      // Handle single source
      if (valueInterpolationSources.length !== 1) {
        return '';
      }

      const source = valueInterpolationSources[0];

      if (source === 'marketplaceOffer') {
        return get(selectedMarketplaceOffer, key) || '';
      }
      if (source === 'pressRelease') {
        return get(selectedPressRelease, key) || '';
      }
      if (source === 'graphQlQuery') {
        return get(queryData, key) || '';
      }

      return '';
    };

    // Helper function to interpolate href values
    const interpolateHrefValue = (hrefValue: string): string => {
      const hrefInterpolatedKeys = getInterpolatedKeys(hrefValue);

      if (!hrefInterpolatedKeys || hrefInterpolatedKeys.length === 0) {
        return hrefValue;
      }

      const valueInterpolationSources = interpolationSource.filter(
        (source) => source !== 'interpolatedLink'
      );

      const hrefMatchedValues = hrefInterpolatedKeys.map((key: string) =>
        getInterpolatedValueForKey(key, valueInterpolationSources)
      );

      return replaceInterpolatedValues(hrefValue, hrefMatchedValues);
    };

    // Build a map of link configurations
    const linkConfigMap = interpolatedLinkItems?.reduce<
      Record<
        string,
        { link: string; impressionSection: string; impressionPosition: string }
      >
    >((acc, item) => {
      acc[item.key] = {
        link: item.value,
        impressionSection: item.impressionSection ?? '',
        impressionPosition: item.impressionPosition ?? '',
      };
      return acc;
    }, {});

    // Extract link contents after value interpolation to get interpolated text
    const linkContentsAfterInterpolation = getInterpolatedLinkKeys(
      textAfterValueInterpolation
    );

    // Create anchor tags for each <<...>> pattern
    return interpolatedLinkKeys?.map((originalKey: string, index: number) => {
      const linkConfig = linkConfigMap?.[originalKey];

      if (!linkConfig) {
        return '';
      }

      // Use the content after interpolation as the link text
      // For example: <<{{name}}>> becomes <<Chase Sapphire>> after stage 1
      const linkText = linkContentsAfterInterpolation[index] || originalKey;

      const { impressionSection, impressionPosition } = linkConfig;
      const interpolatedHref = interpolateHrefValue(linkConfig.link);

      const updatedMarketPlaceEntity = {
        ...selectedMarketplaceOffer,
        cta: {
          ...selectedMarketplaceOffer?.cta,
          link: interpolatedHref,
        },
      };

      const updatedMarketPlaceEntityWithApplyNowLinkTracking =
        addCtaApplyNowLinkTracking(
          updatedMarketPlaceEntity,
          marketplaceButtonProps
        );

      const hrefWithApplyNowLinkTracking =
        updatedMarketPlaceEntityWithApplyNowLinkTracking?.cta?.link;

      let link = enableApplyNowButtonTracking
        ? hrefWithApplyNowLinkTracking
        : interpolatedHref;

      if (captureCustomQueryParams && link) {
        const urlWithParams = buttonUrlWithCustomQueryParams(
          link,
          searchParams
        );
        link =
          typeof urlWithParams === 'string'
            ? urlWithParams
            : urlWithParams.toString();
      }

      return `<a href="${link}" class="interpolated-link" data-link-key="${originalKey}" data-impression-section="${impressionSection}" data-impression-position="${impressionPosition}" rel="noopener noreferrer">${linkText}</a>`;
    });
  }, [
    interpolationSource,
    interpolatedLinkItems,
    textAfterValueInterpolation,
    interpolatedLinkKeys,
    selectedMarketplaceOffer,
    selectedPressRelease,
    queryData,
    marketplaceButtonProps,
    enableApplyNowButtonTracking,
    captureCustomQueryParams,
    searchParams,
  ]);

  // Replace <<...>> patterns with anchor tags
  const textAfterLinkInterpolation = replaceInterpolatedLinks(
    textAfterValueInterpolation,
    linkReplacements
  );

  const textToShow =
    interpolationSource.length > 0 &&
    (interpolatedKeys?.length || interpolatedLinkKeys?.length)
      ? textAfterLinkInterpolation
      : text;

  const hasInterpolatedLinks = interpolationSource.includes('interpolatedLink');

  return {
    textToShow,
    hasInterpolatedLinks,
  };
};
